import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import set from 'ember-set-helper/helpers/set';
import {
  HdsPageHeader,
  HdsTextDisplay,
  HdsButton,
  HdsSeparator,
  HdsTextBody,
} from '@hashicorp/design-system-components/components';
import { HashicorpCloudResourcemanagerPolicyBindingMemberType } from '@clients/cloud-resource-manager';
import { CoreLayoutMain, BreadcrumbsContainer, Flex } from 'core/components';
import { FormValidationError } from 'core/utils/form-validation';
import { supportTicketPrefillHref } from 'core/utils/support-ticket';
import { PROJECT_IAM_ASSIGNMENT } from 'core/utils/consts/analytics-events/platform';
import SelectGroupRole from 'hcp/components/iam/groups/project/form/select-group-role';
import SelectGroup from 'hcp/components/iam/groups/project/modal/select-group';
import getProjectIamAssignmentTrackingMetadata from 'hcp/utils/iam/get-project-iam-assignment-tracking-metadata';

/**
 *
 * `PageGroupsProjectAdd`
 *
 *
 * ```
 * <Page::Groups::Project::Add />
 * ```
 *
 * @class PageGroupsProjectAdd
 *
 */
export default class PageGroupsProjectAdd extends Component {
  @service api;
  @service flashMessages;
  @service intl;
  @service router;
  @service analytics;

  @tracked groupIdToRoleIdMappings = {};
  @tracked showSelectModal = false;

  handleSelectGroupSubmit = task(async (formState) => {
    this.groupIdToRoleIdMappings = formState.selectedGroupIds.reduce(
      (mappings, resourceId) =>
        this.groupIdToRoleIdMappings[resourceId] === undefined
          ? { ...mappings, [resourceId]: 'roles/viewer' }
          : {
              ...mappings,
              [resourceId]: this.groupIdToRoleIdMappings[resourceId],
            },
      {}
    );

    this.showSelectModal = false;
  });

  get selectedGroupIds() {
    return Object.keys(this.groupIdToRoleIdMappings);
  }

  get supportTicketHref() {
    return supportTicketPrefillHref(this.userContext);
  }

  handleSelectGroupCancel = () => {
    this.showSelectModal = false;
  };

  isSelected = (group) => {
    this.selectedGroupIds.includes(group.resourceId);
  };

  updateGroupRole = (event) => {
    event.preventDefault();
    const { name: resourceId, value: roleId } = event.target;
    this.groupIdToRoleIdMappings = {
      ...this.groupIdToRoleIdMappings,
      [resourceId]: roleId,
    };
  };

  removeGroup = (resourceId) => {
    delete this.groupIdToRoleIdMappings[resourceId];
    this.groupIdToRoleIdMappings = {
      ...this.groupIdToRoleIdMappings,
    };
  };

  handleSelectGroupRoleSubmit = task(async (policy) => {
    const hasGroupsToAdd = !!Object.keys(this.groupIdToRoleIdMappings).length;
    if (!hasGroupsToAdd) {
      throw new FormValidationError(
        this.intl.t('components.page.access-control.groups.add.error-empty')
      );
    }

    const { project } = this.args;

    const groupNames = this.args.groups
      .filter((group) =>
        Object.keys(this.groupIdToRoleIdMappings).includes(group.resourceId)
      )
      .map((group) => group.displayName);

    try {
      await this.api.resourceManager.project.projectServiceSetIamPolicy(
        project.id,
        { policy }
      );

      this.args?.groups?.forEach((group) => {
        const trackingMetadata = getProjectIamAssignmentTrackingMetadata(
          HashicorpCloudResourcemanagerPolicyBindingMemberType.GROUP,
          'add',
          policy,
          this.args.policy,
          project?.id,
          group?.resourceId
        );

        this.analytics.trackEvent(PROJECT_IAM_ASSIGNMENT, trackingMetadata);
      });

      this.flashMessages.success(
        this.intl.t(`components.page.access-control.groups.add.success.title`),
        {
          content: this.intl.t(
            `components.page.access-control.groups.add.success.content`,
            {
              groupCount: groupNames.length,
              groupNames: groupNames.join(', '),
              projectName: project.name,
              htmlSafe: true,
            }
          ),
        }
      );

      getOwner(this)
        .lookup('route:cloud.access-control.groups.list')
        ?.refresh();
      this.router.transitionTo('cloud.access-control.groups.list');
    } catch {
      this.flashMessages.error(
        this.intl.t('components.page.access-control.groups.add.error.title'),
        {
          content: this.intl.t(
            'components.page.groups.project.edit.flash-messages.something-went-wrong',
            { groupName: groupNames.join(', ') }
          ),
        }
      );
    }
  });

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |P|>
          <P.Breadcrumb>
            <BreadcrumbsContainer />
          </P.Breadcrumb>
          <P.Title>
            {{t
              "components.page.access-control.groups.add.title"
              projectName=@project.name
              htmlSafe=true
            }}
          </P.Title>
          <P.IconTile @icon="users" @iconSecondary="plus" />
          <P.Description>
            {{t "components.page.access-control.groups.add.description"}}
          </P.Description>
        </HdsPageHeader>
      </:header>
      <:body>
        <Flex @alignItems="start" @direction="column" @gap="md">
          <HdsTextDisplay
            @color="strong"
            @size="200"
            @tag="h3"
            @weight="semibold"
          >
            {{t "components.page.access-control.groups.add.select.label"}}
          </HdsTextDisplay>
          <HdsButton
            @color="secondary"
            @text={{t
              "components.page.access-control.groups.add.select.button"
            }}
            data-test-groups-select-button
            {{on "click" (set this "showSelectModal" true)}}
          />
        </Flex>
        <HdsSeparator />
        {{#if this.selectedGroupIds.length}}
          <SelectGroupRole
            @currentRolesLocation={{@currentRolesLocation}}
            @currentScopeLink={{@currentScopeLink}}
            @groups={{@groups}}
            @projectRoles={{@projectRoles}}
            @policy={{@policy}}
            @project={{@project}}
            @groupIdToRoleIdMappings={{this.groupIdToRoleIdMappings}}
            @removeGroup={{this.removeGroup}}
            @handleSubmit={{this.handleSelectGroupRoleSubmit}}
          />
        {{else}}
          <HdsTextBody @color="primary" @size="200" @weight="regular">
            {{t "components.page.access-control.groups.add.assign.empty"}}
          </HdsTextBody>
        {{/if}}
      </:body>
    </CoreLayoutMain>

    {{#if this.showSelectModal}}
      <SelectGroup
        @candidateGroups={{@groupsWithoutProjectBindings}}
        @initialSelectedGroupIds={{this.selectedGroupIds}}
        @handleSubmit={{this.handleSelectGroupSubmit}}
        @handleCancel={{this.handleSelectGroupCancel}}
      />
    {{/if}}
  </template>
}
